import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
import { PRODUCTS } from '../lib/constants';

const STORAGE_KEY = 'appmando_popup_v1';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 días
const DELAY_MS = 2500;
const ACCENT = '#2563EB';

const appmando = PRODUCTS.find((p) => p.id === 'appmando') || {};
const APPSTORE_URL = appmando.appStoreUrl || 'https://apps.apple.com/es/app/id6774574403';
const PRODUCT_PATH = appmando.href || '/productos/appmando';
const ICON = appmando.logo || '/appmando/appmando-icon.png';

// Modal propio (NO Radix): el cierre depende de onClick directo, que sí se
// sintetiza desde un tap en iOS Safari. Mantiene aria-modal, Esc, focus básico
// y scroll-lock del body.
const AppMandoPopup = () => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const reduceMotion = useReducedMotion();
  const closeBtnRef = useRef(null);

  // Mounted-gate: el modal SOLO existe en el navegador, nunca en el prerender ni
  // en el primer render de hidratación → evita que createPortal participe en la
  // hidratación (sobre el document.body ya poblado) y rompa los listeners de cierre.
  useEffect(() => {
    setMounted(true);
  }, []);

  // Aparición retrasada con cooldown de 7 días
  useEffect(() => {
    // Durante el prerender el timer NO arranca → el modal nunca se abre ni se
    // captura en el HTML estático (determinista, independiente del timing de red).
    if (typeof window !== 'undefined' && window.__PRERENDER__) return;
    try {
      const last = localStorage.getItem(STORAGE_KEY);
      if (last && Date.now() - Number(last) < COOLDOWN_MS) return;
    } catch (e) {
      /* localStorage no disponible: continuar y mostrar */
    }
    const timer = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {
      /* noop */
    }
    setOpen(false);
  }, []);

  // Scroll-lock del body + cierre por Esc + foco inicial, solo con el modal abierto
  useEffect(() => {
    if (!open) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);
    const focusTimer = setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      clearTimeout(focusTimer);
    };
  }, [open, dismiss]);

  const overlayMotion = reduceMotion
    ? { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.3 } };

  const cardMotion = reduceMotion
    ? { initial: { opacity: 1, scale: 1, y: 0 }, animate: { opacity: 1, scale: 1, y: 0 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, scale: 0.9, y: 20 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: 10 },
        transition: { type: 'spring', stiffness: 260, damping: 22 },
      };

  // Hasta que el componente no se ha montado en cliente, no renderiza nada
  // (ni en prerender ni en la 1ª pasada de hidratación).
  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          {...overlayMotion}
          onClick={dismiss}
          role="presentation"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        >
          <motion.div
            {...cardMotion}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="appmando-popup-title"
            aria-describedby="appmando-popup-desc"
            className="relative max-h-[90vh] w-full max-w-[420px] overflow-y-auto glass-card p-7 text-center sm:p-8"
            style={{ borderColor: `${ACCENT}40` }}
          >
            {/* Cerrar */}
            <button
              ref={closeBtnRef}
              type="button"
              onClick={dismiss}
              aria-label="Cerrar"
              className="absolute right-3 top-3 flex h-11 w-11 touch-manipulation items-center justify-center rounded-full text-[#8892A4] transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#2563EB]/50"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Badge */}
            <div
              className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold"
              style={{ borderColor: `${ACCENT}40`, color: ACCENT, background: `${ACCENT}14` }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
              App nativa · Ya disponible
            </div>

            {/* Icono */}
            <img
              src={ICON}
              alt="AppMando"
              width={64}
              height={64}
              className="mx-auto mb-5 h-16 w-16 rounded-2xl object-cover"
              style={{ boxShadow: `0 0 28px ${ACCENT}66` }}
            />

            {/* Título */}
            <h2
              id="appmando-popup-title"
              className="mb-3 text-2xl font-black text-[#F0F4FF]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Conoce AppMando
            </h2>

            {/* Texto */}
            <p id="appmando-popup-desc" className="mb-6 text-sm leading-relaxed text-[#8892A4]">
              El marketplace que conecta tu hogar con profesionales de confianza en toda España.
              ¿Buscas un profesional? Lo encuentras en minutos. ¿Eres profesional? Te llegan
              clientes que ya te buscan.
            </p>

            {/* CTA principal */}
            <Link
              to={PRODUCT_PATH}
              onClick={dismiss}
              className="flex w-full touch-manipulation items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base font-bold text-white transition-all hover:scale-[1.02] hover:opacity-95"
              style={{ background: `linear-gradient(135deg, ${ACCENT}, #1B93A4)`, boxShadow: `0 0 20px ${ACCENT}40` }}
            >
              Descubrir AppMando
            </Link>

            {/* App Store badge */}
            <a
              href={APPSTORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={dismiss}
              aria-label="Descargar AppMando en el App Store"
              className="mt-4 inline-flex touch-manipulation items-center justify-center"
            >
              <img src="/appmando/app-store-es.svg" alt="Descárgalo en el App Store" className="h-11 w-auto" />
            </a>

            <p className="mt-4 flex items-center justify-center gap-1 text-[11px] text-[#8892A4]/70">
              Un producto de Leapifyes <ExternalLink className="h-3 w-3" />
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AppMandoPopup;
